import { GreyStrip } from "@coworker/app/src/components/RoomSettings/Atoms/GreyStrip";
import { isEmptyCombinationArticle } from "@coworker/app/src/components/RoomSettings/Helpers/articleHelpers";
import { LocalCombinationAccordion } from "@coworker/app/src/components/RoomSettings/Molecules/LocalCombinationAccordion";
import {
  Article,
  EdsData,
  SalesStopData,
} from "@coworker/app/src/components/RoomSettings/types/article";
import { LocalCombinationsItemsListViewItem } from "@coworker/apprestructured/src/media-maintenance/components/LocalCombinations/LocalCombinationsItemsListViewItem/LocalCombinationsItemsListViewItem";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import { ItemsStock } from "@coworker/apprestructured/src/shared/hooks/item/useItemsStock";
import { useTranslation } from "@coworker/locales";
import { useState } from "react";
import styles from "./local-combination-items-list-view.module.css";

type LocalCombinationItemsListViewProps = {
  parentArticle: Article;
  combinationArticles: { [key: string]: Article[] };
  edsData: EdsData[] | null;
  salesStopData: SalesStopData[] | undefined;
  mediaItemViewType: MediaItemViewType;
  itemsStockInfo?: ItemsStock;
};

// articlesCountInCombination can be returned by the API as a fixed-decimal
// numeric string (e.g. "1.50"), so it must be parsed before use.
const getSubArticleQuantity = (article: Article): number => {
  const raw = article.articlesCountInCombination as unknown;
  if (raw == null) return 1;
  if (typeof raw === "string" && raw.trim() === "") return 1;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 1;
};

export const LocalCombinationItemsListView = ({
  parentArticle,
  combinationArticles,
  edsData,
  salesStopData,
  mediaItemViewType = MediaItemViewType.SIMPLE_ITEM_VIEW,
  itemsStockInfo,
}: LocalCombinationItemsListViewProps) => {
  const { t } = useTranslation();
  const [openAccordions, setOpenAccordions] = useState<{
    [key: string]: boolean;
  }>({}); // State to track open accordions

  const toggleAccordion = (id: string) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [id]: !prev[id], // Toggle the state of the specific accordion
    }));
  };

  const articleCount =
    combinationArticles?.[parentArticle?.productArticleId || ""]?.length || 0;

  const piecesCount =
    combinationArticles?.[parentArticle?.productArticleId || ""]?.reduce(
      (acc, article) =>
        acc + getSubArticleQuantity(article) * parentArticle.nbrArticles,
      0
    ) || 0;
  const roundedPiecesCount = Math.round(piecesCount * 100) / 100;

  if (!parentArticle) {
    return null;
  }

  const isDeletedLocalCombination = isEmptyCombinationArticle(parentArticle);
  return (
    <>
      <LocalCombinationAccordion
        showInfoIcon={isDeletedLocalCombination}
        key={`local-combination-${parentArticle.id}`}
        isOpen={openAccordions[parentArticle.id] ?? false} // Check if the accordion is open
        title={
          isDeletedLocalCombination
            ? t("localCombinationDeletedString")
            : t("localCombinationCountString", {
                count: articleCount,
              })
        }
        onClick={
          isDeletedLocalCombination
            ? () => {}
            : () => {
                toggleAccordion(parentArticle.id);
              }
        }
      />
      {openAccordions[parentArticle.id] && (
        <>
          <div className={styles["list-view-item-root"]}>
            {combinationArticles?.[parentArticle.productArticleId]?.map(
              (article: Article) => (
                <LocalCombinationsItemsListViewItem
                  stockInfo={itemsStockInfo?.[article.productArticleId]}
                  mediaItemViewType={mediaItemViewType}
                  itemNo={article.productArticleId}
                  article={article}
                  totalQuantityPieces={
                    getSubArticleQuantity(article) * parentArticle.nbrArticles
                  }
                  edsData={
                    edsData?.find(
                      (eds: { productArticleId: string }) =>
                        eds.productArticleId === article.productArticleId
                    ) ?? null
                  }
                  salesStopData={
                    salesStopData?.find(
                      (salesStop) =>
                        salesStop.productArticleId === article.productArticleId
                    ) ?? null
                  }
                  badges4AK1={article.badges4AK1 ?? []}
                  itemTags={article.itemTags ?? []}
                />
              )
            )}
          </div>
          <div className={styles["total-container"]}>
            <GreyStrip
              text={`${t("localCombinationGreyStripArticleString", {
                count: articleCount,
              })}, ${t("localCombinationGreyStripCountString", {
                count: roundedPiecesCount,
              })}`}
              bold={false}
            />
          </div>
        </>
      )}
    </>
  );
};
